import { concat, hash } from '@ember/helper';
import { action } from '@ember/object';
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import { focusTrap } from 'ember-focus-trap';
import { t } from 'ember-intl';
import onKey from 'ember-keyboard/modifiers/on-key';
import and from 'ember-truth-helpers/helpers/and';
import BvBackdrop from 'volta/components/bv-backdrop';
import BvBtn from 'volta/components/bv-btn';
import BvModalBody from 'volta/components/bv-modal/body';
import BvModalFooter from 'volta/components/bv-modal/footer';
import BvModalHeader from 'volta/components/bv-modal/header';
import BvModalSection from 'volta/components/bv-modal/section';
import BvModalSectionHeader from 'volta/components/bv-modal/section-header';
import classNames from 'volta/helpers/class-names';
import orElse from 'volta/helpers/or-else';
import { closest, onceAnimationEnd } from 'volta/utils/dom-utils';

import type { ComponentLike } from '@glint/template';
import type { FocusTrapOptions } from 'global';
import type { BvModalBodySignature } from 'volta/components/bv-modal/body';
import type { BvModalFooterSignature } from 'volta/components/bv-modal/footer';
import type { BvModalSectionSignature } from 'volta/components/bv-modal/section';
import type { BvModalSectionHeaderSignature } from 'volta/components/bv-modal/section-header';
import type { BvModalHeaderSignature } from './header';
import cssVars from 'volta/modifiers/css-vars';

type ISize = 'xs' | 'sm' | 'lg' | 'xl' | 'xxl' | 'search';

export interface BvModalSignature {
  Args: {
    subtitle?: string;
    title?: string;
    closeLabel?: string;
    forceDesktopStyle?: boolean;
    dialog?: boolean;
    backdropClassNames?: string;
    hiddenBackdrop?: boolean;
    blurredBackdrop?: boolean;
    onClickOverlay?: () => void;
    onClose?: () => void;
    onInsert?: (context: TContext) => void;
    size?: ISize;
    sheet?: boolean;
    sheetSide?: 'left' | 'right';
    /**
     * Shows the close button in the header
     * or on the side of a sheet modal
     * @default true;
     **/
    showDismiss?: boolean;
    translucentBackdrop?: boolean;
    isLoading?: boolean;
    isShowing?: boolean;
    escapable?: boolean;
    closingDelay?: number;
    isFocusTrapActive?: boolean;
    shouldSelfFocus?: boolean;
    isFocusTrapPaused?: boolean;
    focusTrapOptions?: FocusTrapOptions;
    belowAppHeader?: boolean;
    initialFocusElement?:
      | HTMLElement
      | SVGElement
      | string
      | false
      | undefined
      | (() => HTMLElement | SVGElement | string | false | undefined);
  };
  Blocks: {
    default: [
      {
        Header: ComponentLike<BvModalHeaderSignature>;
        Body: ComponentLike<BvModalBodySignature>;
        Footer: ComponentLike<BvModalFooterSignature>;
        Section: ComponentLike<BvModalSectionSignature>;
        SectionHeader: ComponentLike<BvModalSectionHeaderSignature>;
      }
    ];
  };
  Element: HTMLDivElement;
}

type TContext = {
  isShowing: boolean;
  isWarning: boolean;
  isLoading: boolean;
  close: () => void;
};

export default class BvModalComponent extends Component<BvModalSignature> {
  @tracked isShowing = true;
  @tracked isWarning = false;
  modalEl!: HTMLElement;

  get additionalFocusElements() {
    return ['#ember-basic-dropdown-wormhole', '.ember-basic-dropdown-content'].filter(
      (e) => document.querySelector(e) != null
    );
  }

  get context(): TContext {
    return {
      isShowing: this.isShowing,
      isWarning: this.isWarning,
      isLoading: this.args.isLoading ?? false,
      close: () => {
        return this.close.perform();
      }
    };
  }

  get escapable() {
    return this.args.escapable ?? true;
  }

  get belowAppHeader() {
    return this.args.belowAppHeader ?? this.args.translucentBackdrop;
  }

  get sizePx() {
    return typeof this.args.size === 'number' ? this.args.size : undefined;
  }

  @action
  attemptEscape(event?: KeyboardEvent | PointerEvent) {
    if (event && this.eventIsFromNestedDropdownOrModal(event)) {
      return;
    }

    if (this.escapable) {
      this.close.perform();
    } else {
      this.warn.perform();
    }
  }

  insert = task(async (el: HTMLElement) => {
    this.modalEl = el;
    await onceAnimationEnd(this.modalEl, 'bv-modal--is-showing');
    this.args.onInsert?.(this.context);
  });

  close = task(async () => {
    if (this.isDestroyed || this.isDestroying) {
      return;
    }
    await onceAnimationEnd(this.modalEl, 'bv-modal--is-hiding');
    this.isShowing = false;
    this.args.onClose?.();
  });

  warn = task(async () => {
    if (this.isDestroyed || this.isDestroying) {
      return;
    }
    if (!this.isShowing) {
      return;
    }

    this.isWarning = true;
    await onceAnimationEnd(this.modalEl, 'bv-modal--is-warning');
    this.isWarning = false;
  });

  private eventIsFromNestedDropdownOrModal(event: KeyboardEvent | PointerEvent) {
    const maybeModalEl = closest(event.target as HTMLElement, '.bv-modal');
    return (
      closest(event.target as HTMLElement, '.ember-basic-dropdown-trigger[aria-expanded="true"]') ||
      closest(event.target as HTMLElement, '.ember-basic-dropdown-content') ||
      (maybeModalEl && maybeModalEl != this.modalEl)
    );
  }

  <template>
    <BvBackdrop
      @transparent={{@translucentBackdrop}}
      @belowAppHeader={{this.belowAppHeader}}
      @onClick={{orElse @onClickOverlay this.attemptEscape}}
      @hidden={{@hiddenBackdrop}}
      @blurred={{orElse @blurredBackdrop false}}
      class={{classNames
        'bv-modal-backdrop'
        (if @dialog 'bv-modal-backdrop--dialog')
        @backdropClassNames
      }}
    >
      {{! template-lint-disable no-at-ember-render-modifiers }}
      <div
        class={{classNames
          'bv-modal'
          (concat 'bv-modal--' (orElse @size 'md'))
          (if @sheet 'bv-modal--sheet')
          (if (and @sheet @sheetSide) (concat 'bv-modal--sheet-' @sheetSide))
          (if this.belowAppHeader 'bv-modal--below-header')
          (if @forceDesktopStyle 'bv-modal--force-desktop-style' 'bv-modal--responsive')
          (if this.isWarning 'bv-modal--is-warning')
          (if this.context.isLoading 'bv-modal--is-loading')
        }}
        {{cssVars bv-modal-size-px=this.sizePx}}
        tabindex='0'
        ...attributes
        {{focusTrap
          isActive=(orElse @isFocusTrapActive true)
          shouldSelfFocus=(orElse @shouldSelfFocus true)
          isPaused=(orElse @isFocusTrapPaused false)
          additionalElements=this.additionalFocusElements
          focusTrapOptions=(orElse
            @focusTrapOptions
            (hash
              clickOutsideDeactivates=true
              escapeDeactivates=false
              initialFocus=@initialFocusElement
              preventScroll=true
            )
          )
        }}
        {{didInsert (perform this.insert)}}
        {{onKey 'Escape' this.attemptEscape}}
      >
        {{#if (and @sheet @showDismiss)}}
          <BvBtn
            class='bv-modal__sheet-dismiss'
            @onClick={{perform this.close}}
            @iconLeft='close'
            @size='sm'
            @color='hud'
            @tooltipText={{orElse @closeLabel (t 'close')}}
          />
        {{/if}}
        {{yield
          (hash
            Header=(component
              BvModalHeader
              onClose=(perform this.close)
              title=@title
              subtitle=@subtitle
              hideCloseButton=(and @sheet @showDismiss)
            )
            Body=(component BvModalBody scrollable=@sheet)
            Footer=(component BvModalFooter)
            Section=(component BvModalSection)
            SectionHeader=(component BvModalSectionHeader)
          )
        }}
      </div>
    </BvBackdrop>
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvModal: typeof BvModalComponent;
    'bv-modal': typeof BvModalComponent;
  }
}
